import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { TextInput } from '@ncr-retail/base-retail-components';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { IForgotPasswordErrors } from 'src/common/interfaces/dtos/IForgotPasswordErrors';
import { ILoginUser } from 'src/common/interfaces/dtos/ILoginUser';
import { IShowPassword } from 'src/common/interfaces/dtos/IShowPassword';
import { IButton } from 'src/common/interfaces/IButton';
import { newStyles } from 'src/touchpoint/components/login-emerald-styles';

const ResetPasswordForm: React.FC<IResetPasswordFormProps> = (
  props: IResetPasswordFormProps,
): JSX.Element => {
  const classes = newStyles();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<IShowPassword>({ displayPassword: false });
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState<IShowPassword>({
    displayPassword: false,
  });
  const { userDetails } = props;

  const canSubmitForm = useCallback((): boolean => {
    let isDisabled = true;
    if (userDetails.password && props.passwordConfirm) {
      if (userDetails.password.length && props.passwordConfirm.length) {
        isDisabled = false;
      }
    }
    return isDisabled;
  }, [userDetails, props.passwordConfirm]);

  useEffect(() => {
    setIsButtonDisabled(canSubmitForm());
  }, [canSubmitForm, userDetails]);

  const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const id = event.target.id;
    const name = event.target.name;
    const value = event.target.value;
    switch (id) {
      case 'password':
        props.setUserDetails({
          ...userDetails,
          [name]: value,
        });
        break;
      case 'passwordConfirm':
        props.setPasswordConfirm(value);
        break;
    }
  };

  const handleClickShowPassword = (type: string): void => {
    if (type === 'newPassword') {
      setShowPassword({
        ...showPassword,
        displayPassword: !showPassword.displayPassword,
      });
    } else {
      setShowPasswordConfirm({
        ...showPasswordConfirm,
        displayPassword: !showPasswordConfirm.displayPassword,
      });
    }
  };
  const handleResetPassword = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (userDetails.password === props.passwordConfirm) {
      props.onSubmit(event);
    } else {
      props.setErrors({ GeneralError: t('ForgotPasswordMisMatchMessage') });
    }
  };

  return (
    <form
      className={classes.registerform}
      onSubmit={handleResetPassword}
      noValidate
      data-testid='FormSubmit'
    >
      <Grid container>
        <Grid item sm={12} className={classes.registerFieldRegistration}>
          <TextInput
            classes={{
              root: classes.registerFieldUnderline,
            }}
            id='password'
            variant='standard'
            margin='none'
            fullWidth
            name='password'
            inputProps={{ 'data-testid': 'reset-password-input', className: 'global-keyboard' }}
            label='New Pin'
            type={showPassword.displayPassword ? 'text' : 'password'}
            onChange={onTextChange}
            value={userDetails.password}
            autoComplete='new-password'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    className={classes.whiteiconbutton}
                    edge='end'
                    aria-label='Toggle pin visibility'
                    onClick={() => handleClickShowPassword('newPassword')}
                    tabIndex={-1}
                    data-testid='toggle-password-btn'
                  >
                    {showPassword.displayPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item sm={12} className={classes.registerFieldRegistration}>
          <TextInput
            classes={{
              root: classes.registerFieldUnderline,
            }}
            id='passwordConfirm'
            variant='standard'
            margin='none'
            fullWidth
            name='passwordConfirm'
            inputProps={{
              'data-testid': 'reset-password-confirm-input',
              className: 'global-keyboard',
            }}
            label='Confirm New Pin'
            type={showPasswordConfirm.displayPassword ? 'text' : 'password'}
            onChange={onTextChange}
            value={props.passwordConfirm}
            autoComplete='new-password'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    className={classes.whiteiconbutton}
                    edge='end'
                    aria-label='Toggle pin visibility'
                    onClick={() => handleClickShowPassword('confirmNewPassword')}
                    tabIndex={-1}
                    data-testid='toggle-password-confirm-btn'
                  >
                    {showPassword.displayPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item sm={6}>
          <BaseButton
            buttonType='white'
            className={`${classes.cancelbutton} ${classes.buttondefaultfont}`}
            fullWidth
            data-testid='cancel-btn'
            type='reset'
            onClick={() => props.cancelButton.onClick()}
            tabIndex={-1}
            disabled={props.cancelButton.disabled}
          >
            {props.cancelButton.label}
          </BaseButton>
        </Grid>
        <Grid item sm={6}>
          <BaseButton
            buttonType='primary'
            className={`${classes.bluebutton} ${classes.buttonwhitefont}`}
            fullWidth
            data-testid='submit-btn'
            type='submit'
            tabIndex={-1}
            disabled={isButtonDisabled}
          >
            {t('SignInView_SupervisorReset')}
          </BaseButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default ResetPasswordForm;

export interface IResetPasswordFormProps {
  cancelButton: IButton;
  submitButton?: IButton;
  errors: IForgotPasswordErrors;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  setUserDetails: (user: ILoginUser) => void;
  userDetails: ILoginUser;
  passwordConfirm: string;
  setPasswordConfirm: (confirmPassword: string) => void;
  setErrors: (state: IForgotPasswordErrors) => void;
}
